import useSWR from "swr"
import useSWRInfinite from "swr/infinite"

import { LCTOnlyDatasets } from "../../../constants/enums"
import { useGetUserData } from "../../../hooks/useGetUserData"
import { canAccessProtectedRoutes } from "../../../utils/auth/authUtils"
import { getClientProtected, getClientPublic } from "../clients/clients"
import {
    searchDemandDevicesMapper,
    IDemDatabaseSearchResults,
} from "../mappers/searchDemandDevicesMapper"
import { SEARCH_CONFIG } from "../swrConfig"
import { SEARCH_DEMAND_DEVICES } from "../urls/hybrid"

export const DEMAND_PAGE_SIZE = 20

export type TUseSearchDemandDevicesPage = {
    data: IDemDatabaseSearchResults | undefined
    isLoading: boolean
    isError: boolean
    isValidating: boolean
    mutate: () => void
}

const getSearchDemandDevicesUrl = (
    searchParams: URLSearchParams,
    dataset: LCTOnlyDatasets,
    canAccessProtected: boolean,
    pageIndex: number,
    pageSize: number
): string => {
    const queryParams = new URLSearchParams(searchParams.toString())
    queryParams.set("skip", String(pageIndex * pageSize))
    queryParams.set("limit", String(pageSize))
    return SEARCH_DEMAND_DEVICES(
        queryParams.toString(),
        dataset,
        canAccessProtected
    )
}

/**
 * Paged variant of {@link useSearchDemandDevices}. Use when the consumer
 * displays a single page at a time and changes page via a controlled
 * pagination model (e.g. the device search panels). Mirrors
 * {@link useSearchGenDevicesPage}.
 */
export const useSearchDemandDevicesPage = (
    searchParams: URLSearchParams,
    dataset: LCTOnlyDatasets,
    pageIndex: number,
    pageSize = DEMAND_PAGE_SIZE
): TUseSearchDemandDevicesPage => {
    const { isSignedIn, groups } = useGetUserData()
    const canAccessProtected = canAccessProtectedRoutes(isSignedIn, groups)

    const url = getSearchDemandDevicesUrl(
        searchParams,
        dataset,
        canAccessProtected,
        pageIndex,
        pageSize
    )

    const { data, error, isLoading, isValidating, mutate } = useSWR(
        url,
        async (requestUrl: string): Promise<IDemDatabaseSearchResults> => {
            try {
                const fetcher = canAccessProtected
                    ? getClientProtected
                    : getClientPublic
                const res = await fetcher(requestUrl)
                return searchDemandDevicesMapper.map(res, dataset)
            } catch {
                throw Error()
            }
        },
        SEARCH_CONFIG
    )

    return {
        data,
        isLoading,
        isError: error,
        isValidating,
        mutate,
    }
}

type TUseSearchDemandDevices = {
    data: IDemDatabaseSearchResults | undefined
    isLoading: boolean
    isError: boolean
    isValidating: boolean
    page: number
    setPage: (
        size: number | ((_size: number) => number)
    ) => Promise<IDemDatabaseSearchResults[] | undefined>
    isFinalPage: (pageIndex: number, totalResults: number) => boolean
    mutate: () => void
    canAccessProtected: boolean
}

/**
 * Hook for calling search API for demand devices. Checks authentication
 * status to determine if public / protected API should be used. Public users
 * should not be directed to the protected routes.
 * @returns {TUseSearchDemandDevices} - SWR data, loading and error states.
 */
const useSearchDemandDevices = (
    searchParams: URLSearchParams,
    dataset: LCTOnlyDatasets
): TUseSearchDemandDevices => {
    const { isSignedIn, groups } = useGetUserData()
    const canAccessProtected = canAccessProtectedRoutes(isSignedIn, groups)
    const isFinalPage = (pageIndex: number, totalResults: number): boolean =>
        pageIndex >= Math.ceil(totalResults / DEMAND_PAGE_SIZE)
    /**
     * Generates URL query key for pagination. If auth status is configuring,
     * the request should be skipped. Once authentication is complete the hook
     * will rerender, at which point an API request will be made.
     * @param pageIndex - index of current page
     * @param previousPageData  - data output from previous page
     * @returns
     */
    const getKey = (
        pageIndex: number,
        previousPageData: IDemDatabaseSearchResults
    ): string | null => {
        if (previousPageData) {
            if (isFinalPage(pageIndex, previousPageData.totalResults)) {
                return null
            }
        }
        // Page information should not be appended onto global URL params. A
        // separate local params object is instantiated to avoid mutating global state
        const queryParams = new URLSearchParams(searchParams.toString())
        queryParams.set("skip", String(pageIndex * DEMAND_PAGE_SIZE))
        queryParams.set("limit", String(DEMAND_PAGE_SIZE))
        return SEARCH_DEMAND_DEVICES(
            queryParams.toString(),
            dataset,
            canAccessProtected
        )
    }

    const {
        data: pageResults,
        error,
        isLoading,
        isValidating,
        size,
        setSize,
        mutate,
    } = useSWRInfinite(
        getKey,
        async (url: string): Promise<IDemDatabaseSearchResults> => {
            try {
                const fetcher = canAccessProtected
                    ? getClientProtected
                    : getClientPublic
                const res = await fetcher(url)
                return searchDemandDevicesMapper.map(res, dataset)
            } catch {
                throw Error()
            }
        },
        SEARCH_CONFIG
    )

    return {
        data: searchDemandDevicesMapper.mergePaginatedData(pageResults),
        isLoading,
        isError: error,
        isValidating,
        page: size,
        setPage: setSize,
        isFinalPage,
        mutate,
        canAccessProtected,
    }
}

export default useSearchDemandDevices
